import { useEffect, useState, useMemo } from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import ModeEditOutlineTwoToneIcon from "@mui/icons-material/ModeEditOutlineTwoTone";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  styled,
  useTheme,
  TextField,
  MenuItem,
  Stack,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import Refresh from "@mui/icons-material/Refresh";
import {
  getAllDoctors,
  getDoctorById,
  deactivateDoctor,
  activateDoctor,
} from "../../../services/doctorService.js";
import { toast } from "react-toastify";
import { confirmAsync } from "../../../utils/appNotify";
import { getAllDepartment } from "../../../services/departmentService.js";
import AddNewDoctor from "./AddNewDoctor.jsx";
import subscriptionRoleLimitsService from "../../../services/subscriptionRoleLimitsService";
import { useAuth } from "../../../context/AuthContext";
import { useSubscriptionCapabilities } from "../../../hooks/useSubscriptionCapabilities";
import { filterDepartmentsForPackage } from "../../../utils/subscriptionScopeUtils";
import axios from "axios";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import DownloadIcon from "@mui/icons-material/Download";
import dayjs from "dayjs";
import { getOrganizationName } from "../../../utils/organizationUtils";
import { downloadStyledTableExcel } from "../../../utils/patientTableExcelExport";
import {
  TABLE_ACTIONS_ROW_SX,
  TABLE_ADD_BUTTON_SX,
  TABLE_ICON_BUTTON_SX,
  TABLE_ICON_SX,
  TABLE_SEARCH_ROW_SX,
} from "../../../utils/pageLayoutStyles";

// Base columns without password
const baseColumns = [
  { id: "id", label: "Doctor Id" },
  { id: "name", label: "Doctor Name" },
  { id: "mobile", label: "Mobile" },
  { id: "email", label: "Email" },
  { id: "aadhar", label: "Aadhar" },
  { id: "education", label: "Education" },
  { id: "specialty", label: "Specialty" },
  { id: "registrationNumber", label: "Registration Number" },
  { id: "userRole", label: "Role" },
  { id: "departmentName", label: "Department Name" },
  { id: "joiningDate", label: "Joining Date" },
  { id: "status", label: "Status" },
];

const normalizeRole = (role) => (role || "").toLowerCase().replace(/[_\s]+/g, "");

const isAdminOrSuperAdmin = (userRole) => {
  const r = normalizeRole(userRole);
  return r === "admin" || r === "superadmin";
};

const isDoctorRecordActive = (doctor) => {
  const activeStatus = doctor?.isActive ?? doctor?.is_active;
  return activeStatus !== 0 && activeStatus !== false;
};

const isDoctorOrSurgeonRole = (doctor) =>
  doctor.userRole === "doctor" ||
  doctor.userRole === "surgeon" ||
  doctor.userRole === null ||
  doctor.userRole === undefined;

/** List API often omits these; detail API includes them for admin views. */
const SENSITIVE_DOCTOR_FIELDS = ["mobile", "email", "aadhar", "password"];

const getDoctorFieldValue = (doctor, field) => {
  if (!doctor) return undefined;
  const aliases = {
    mobile: ["mobile", "mobileNumber", "phone", "phoneNumber", "contactNumber"],
    email: ["email", "emailId", "emailAddress"],
    aadhar: ["aadhar", "aadhaar", "aadharNumber", "aadhaarNumber"],
    password: ["password", "user_pwd", "userPwd", "pwd"],
  };
  for (const key of aliases[field] || [field]) {
    const value = doctor[key];
    if (value !== null && value !== undefined && value !== "") {
      return value;
    }
  }
  return doctor[field];
};

const isMissingSensitiveDoctorFields = (doctor) =>
  SENSITIVE_DOCTOR_FIELDS.some((field) => {
    const value = getDoctorFieldValue(doctor, field);
    return value === null || value === undefined || value === "";
  });

const normalizeSensitiveDoctorFields = (doctor) => {
  if (!doctor) return doctor;
  const normalized = { ...doctor };
  for (const field of SENSITIVE_DOCTOR_FIELDS) {
    const value = getDoctorFieldValue(doctor, field);
    if (value !== null && value !== undefined && value !== "") {
      normalized[field] = value;
    }
  }
  return normalized;
};

/** Fetch full doctor details in small batches to avoid UI hangs / rate limits. */
const enrichDoctorsWithSensitiveFields = async (doctors, batchSize = 5) => {
  const enriched = [...doctors];
  for (let i = 0; i < enriched.length; i += batchSize) {
    const batch = enriched.slice(i, i + batchSize);
    const batchResults = await Promise.all(
      batch.map(async (doctor) => {
        if (!doctor?.id || !isMissingSensitiveDoctorFields(doctor)) {
          return normalizeSensitiveDoctorFields(doctor);
        }
        try {
          const fullDetails = await getDoctorById(doctor.id);
          return normalizeSensitiveDoctorFields({ ...doctor, ...fullDetails });
        } catch (error) {
          console.warn(`Failed to fetch full details for doctor ${doctor.id}:`, error);
          return normalizeSensitiveDoctorFields(doctor);
        }
      })
    );
    for (let j = 0; j < batchResults.length; j++) {
      enriched[i + j] = batchResults[j];
    }
  }
  return enriched;
};

// Helper function to get columns based on user role
const getColumns = (userRole) => {
  const normalizedRole = normalizeRole(userRole);
  
  // Only show password for admin and super_admin roles
  const showPassword = normalizedRole === "admin" || normalizedRole === "superadmin";
  
  if (showPassword) {
    // Insert password after mobile
    const columns = [...baseColumns];
    const mobileIndex = columns.findIndex(col => col.id === "mobile");
    columns.splice(mobileIndex + 1, 0, { id: "password", label: "Password" });
    return columns;
  }
  
  return baseColumns;
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
}));

export default function DoctorsTable() {
  const { user } = useAuth();
  const { isPremiumPackage } = useSubscriptionCapabilities();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [fetching, setFetching] = useState({ doctor_list: true });
  const [doctor_list, setDoctorList] = useState([]);
  const [all_doctors, setAllDoctors] = useState([]);
  const theme = useTheme();
  const [open_doctor_modal, setOpenDoctorModal] = useState(false);
  const [doctor_info, setDoctorInfo] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterName, setFilterName] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [doctorLimitReached, setDoctorLimitReached] = useState(false);
  const [checkingLimit, setCheckingLimit] = useState(false);
  const [departmentList, setDepartmentList] = useState([]);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);

  const canManageDoctors = isAdminOrSuperAdmin(user?.roleName);

  const visibleDepartmentFilters = useMemo(
    () =>
      filterDepartmentsForPackage(departmentList, {
        isPremium: isPremiumPackage,
      }),
    [departmentList, isPremiumPackage]
  );

  const resolveOrganizationId = () => {
    const orgIdFromStorage = localStorage.getItem("organizationId");
    if (orgIdFromStorage && !isNaN(orgIdFromStorage) && Number(orgIdFromStorage) > 0) {
      return Number(orgIdFromStorage);
    }
    if (user?.organizationId) {
      return user.organizationId;
    }
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        return parsedUser?.organizationId || null;
      } catch (e) {
        console.error("Error parsing stored user:", e);
      }
    }
    return null;
  };

  const checkDoctorLimit = async (organizationId, doctorsList = null) => {
    if (!organizationId) {
      setDoctorLimitReached(false);
      return;
    }

    setCheckingLimit(true);
    try {
      const limitCheck = await subscriptionRoleLimitsService.checkRoleLimit(
        organizationId,
        "doctor",
        doctorsList ? { doctors: doctorsList } : {}
      );
      setDoctorLimitReached(limitCheck.limit !== -1 && !limitCheck.allowed);
    } catch (error) {
      console.error("Error checking doctor role limit:", error);
      setDoctorLimitReached(false);
    } finally {
      setCheckingLimit(false);
    }
  };

  const handleExport = async () => {
    const rows = doctor_list || [];
    const headers = [
      "Doctor Id",
      "Doctor Name",
      "Mobile",
      "Email",
      "Aadhar",
      "Education",
      "Specialty",
      "Registration Number",
      "Role",
      "Department Name",
      "Joining Date",
    ];
    const tableRows = rows.map((d) => {
      const joiningDate = d.joiningDate
        ? dayjs(d.joiningDate).isValid()
          ? dayjs(d.joiningDate).format("YYYY-MM-DD")
          : d.joiningDate
        : "";
      return [
        d.id,
        d.name,
        d.mobile || "",
        d.email || "",
        d.aadhar || "",
        d.education || "",
        d.specialty || "",
        d.registrationNumber || "",
        d.userRole || "",
        d.departmentName || "",
        joiningDate,
      ];
    });

    await downloadStyledTableExcel({
      filename: `doctors_${dayjs().format("YYYY-MM-DD")}`,
      sheetName: "Doctors",
      title: "Doctors",
      organizationName: getOrganizationName(user),
      tableHeaders: headers,
      tableRows,
    });
  };

  useEffect(() => {
    // PRIORITY: Check localStorage.organizationId FIRST (for super_admin who gets it set during login)
    // This ensures super_admin can fetch data immediately without waiting for AuthContext update
    let orgId = null;
    const orgIdFromStorage = localStorage.getItem("organizationId");
    if (orgIdFromStorage && !isNaN(orgIdFromStorage) && Number(orgIdFromStorage) > 0) {
      orgId = Number(orgIdFromStorage);
      console.log(`[DoctorsTable] Found organizationId in localStorage: ${orgId}`);
    } else {
      // Fallback: Check user object from context
      orgId = user?.organizationId;
      if (!orgId) {
        // Check localStorage.user as well
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            orgId = parsedUser?.organizationId;
          } catch (e) {
            console.error("Error parsing stored user:", e);
          }
        }
      }
    }
    
    // Fetch data if organizationId is available
    // For super_admin, organizationId should be in localStorage from login
    if (orgId) {
      console.log(`[DoctorsTable] OrganizationId available (${orgId}), fetching data immediately`);
      fetchDoctorList();
      fetchDepartments();
    } else {
        // Check if user is super_admin - they need organizationId to fetch data
        const normalizedRole = (user?.roleName || "").toLowerCase();
        const isSuperAdmin = normalizedRole === 'superadmin' || normalizedRole === 'super_admin';
        
        if (isSuperAdmin) {
          // Super admin needs organizationId - wait for it
          console.log("[DoctorsTable] Super admin - waiting for organizationId...");
        const handleOrgIdUpdate = (event) => {
          const { updatedUser } = event.detail;
          if (updatedUser?.organizationId) {
            console.log("[DoctorsTable] OrganizationId updated, fetching data");
            fetchDoctorList();
            fetchDepartments();
            window.removeEventListener('userOrganizationIdUpdated', handleOrgIdUpdate);
          }
        };
        window.addEventListener('userOrganizationIdUpdated', handleOrgIdUpdate);
        
        // Fallback: check again after a delay
        const timeout = setTimeout(() => {
          const recheckOrgId = localStorage.getItem("organizationId");
          if (recheckOrgId && !isNaN(recheckOrgId) && Number(recheckOrgId) > 0) {
            console.log("[DoctorsTable] OrganizationId found on retry, fetching data");
            fetchDoctorList();
            fetchDepartments();
          }
          window.removeEventListener('userOrganizationIdUpdated', handleOrgIdUpdate);
        }, 500);
        
          return () => {
            clearTimeout(timeout);
            window.removeEventListener('userOrganizationIdUpdated', handleOrgIdUpdate);
          };
        } else {
          // Not super_admin and no organizationId - might be base user, fetch anyway
          console.log("[DoctorsTable] No organizationId but not super_admin, fetching data (base user)");
          fetchDoctorList();
          fetchDepartments();
        }
      }
  }, [user, user?.organizationId]);

  // Fetch departments from backend
  const fetchDepartments = async () => {
    try {
      const departments = await getAllDepartment();
      setDepartmentList(Array.isArray(departments) ? departments : []);
    } catch (error) {
      console.error("Error fetching departments:", error);
      setDepartmentList([]);
    }
  };

 // Fetch only active doctors
//  const fetchDoctorList = async () => {
//   setFetching({ ...fetching, doctor_list: true });
//   try {
//     const data = await getAllDoctors();
//     const activeDoctors = data.filter((doctor) => doctor.is_active !== 0);
//     setDoctorList(activeDoctors);
//   } catch (error) {
//     console.error(error);
//   }
//   setFetching({ ...fetching, doctor_list: false });
// };

const fetchDoctorList = async (force = false) => {
  setFetching({ ...fetching, doctor_list: true });
  try {
    const data = await getAllDoctors({ forceRefresh: force });
    // Ensure data is an array
    const dataArray = Array.isArray(data) ? data : [];

    // Include active and deactivated doctors/surgeons (show deactivated rows in table)
    const filteredDoctors = dataArray.filter(
      (doctor) => isDoctorOrSurgeonRole(doctor)
    );

    // List endpoint often omits mobile/email/aadhar/password — enrich from detail API
    // for admin / super_admin so those columns display (batched for performance).
    const doctorsForTable = canManageDoctors
      ? await enrichDoctorsWithSensitiveFields(filteredDoctors)
      : filteredDoctors;

    const sorted = [...doctorsForTable].sort((a, b) => (b.id ?? 0) - (a.id ?? 0));
    setAllDoctors(sorted);
    setDoctorList(sorted);

    const organizationId = resolveOrganizationId();
    if (organizationId) {
      await checkDoctorLimit(organizationId, filteredDoctors);
    }
  } catch (error) {
    console.error("Failed to fetch doctors", error);
    // Set empty array on error to show "No records found" message
    setDoctorList([]);
    setAllDoctors([]);
  } finally {
    setFetching({ ...fetching, doctor_list: false });
  }
};

  useEffect(() => {
    let filtered = [...all_doctors];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((d) => {
        const searchableFields = [
          d.id,
          d.name,
          d.mobile,
          d.email,
          d.aadhar,
          d.education,
          d.specialty,
          d.registrationNumber,
          d.userRole,
          d.departmentName,
        ]
          .filter(Boolean)
          .map((v) => String(v).toLowerCase());
        
        // Also search in formatted joining date
        if (d.joiningDate) {
          const formattedDate = dayjs(d.joiningDate).isValid()
            ? dayjs(d.joiningDate).format("DD-MM-YYYY").toLowerCase()
            : String(d.joiningDate).toLowerCase();
          searchableFields.push(formattedDate);
        }
        
        return searchableFields.some((v) => v.includes(q));
      });
    }
    if (filterName.trim()) {
      const n = filterName.toLowerCase();
      filtered = filtered.filter((d) => {
        const searchableFields = [
          d.id,
          d.name,
          d.mobile,
          d.email,
          d.aadhar,
          d.education,
          d.specialty,
          d.registrationNumber,
          d.userRole,
          d.departmentName,
        ]
          .filter(Boolean)
          .map((v) => String(v).toLowerCase());
        
        // Also search in formatted joining date
        if (d.joiningDate) {
          const formattedDate = dayjs(d.joiningDate).isValid()
            ? dayjs(d.joiningDate).format("DD-MM-YYYY").toLowerCase()
            : String(d.joiningDate).toLowerCase();
          searchableFields.push(formattedDate);
        }
        
        return searchableFields.some((v) => v.includes(n));
      });
    }
    if (filterDepartment) {
      filtered = filtered.filter((d) => String(d.departmentName || "") === filterDepartment);
    }
    if (fromDate || toDate) {
      const from = fromDate ? new Date(fromDate) : null;
      const to = toDate ? new Date(toDate) : null;
      filtered = filtered.filter((d) => {
        const jd = d.joiningDate ? new Date(d.joiningDate) : null;
        if (!jd) return false;
        if (from && jd < from) return false;
        if (to) {
          const toEnd = new Date(to);
          toEnd.setHours(23, 59, 59, 999);
          if (jd > toEnd) return false;
        }
        return true;
      });
    }
    const finalSorted = filtered.sort((a, b) => (b.id ?? 0) - (a.id ?? 0));
    setDoctorList(finalSorted);
    setPage(0);
  }, [searchQuery, filterName, filterDepartment, fromDate, toDate, all_doctors]);

  const clearFilters = () => {
    setSearchQuery("");
    setFilterName("");
    setFilterDepartment("");
    setFromDate("");
    setToDate("");
  };

  const updateDoctorStatusLocally = (doctorId, isActive) => {
    const patch = { isActive, is_active: isActive ? 1 : 0 };
    setAllDoctors((prev) =>
      prev.map((d) => (d.id === doctorId ? { ...d, ...patch } : d))
    );
    setDoctorList((prev) =>
      prev.map((d) => (d.id === doctorId ? { ...d, ...patch } : d))
    );
  };

  const handleDeactivateDoctor = async (row) => {
    const confirmed = await confirmAsync(
      `Deactivate doctor "${row.name}"? They will not be able to log in until reactivated.`,
      { title: "Deactivate doctor", tone: "warning", confirmLabel: "Deactivate" }
    );
    if (!confirmed) {
      return;
    }

    const userId = row.userId ?? row.user_id;
    if (!userId) {
      toast.error("Cannot deactivate: doctor user account id not found.");
      return;
    }

    setStatusUpdatingId(row.id);
    try {
      await deactivateDoctor(row.id, userId);
      updateDoctorStatusLocally(row.id, false);
      toast.success(`Doctor "${row.name}" has been deactivated.`);
    } catch (error) {
      console.error("Failed to deactivate doctor:", error);
      const msg =
        error.response?.data?.message ||
        error.message ||
        "Failed to deactivate doctor";
      toast.error(msg);
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleActivateDoctor = async (row) => {
    const userId = row.userId ?? row.user_id;
    if (!userId) {
      toast.error("Cannot activate: doctor user account id not found.");
      return;
    }

    setStatusUpdatingId(row.id);
    try {
      await activateDoctor(row.id, userId);
      updateDoctorStatusLocally(row.id, true);
      toast.success(`Doctor "${row.name}" has been activated.`);
    } catch (error) {
      console.error("Failed to activate doctor:", error);
      const msg =
        error.response?.data?.message ||
        error.message ||
        "Failed to activate doctor";
      toast.error(msg);
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  return (
    <Grid container>
      <Grid item xs={12} sx={TABLE_ACTIONS_ROW_SX}>
        {canManageDoctors ? (
          <Tooltip
            title={doctorLimitReached ? "Doctor limit reached. Cannot add more doctors." : "Add new doctor"}
          >
            <span>
              <Button
                variant="contained"
                color="primary"
                size="small"
                sx={TABLE_ADD_BUTTON_SX}
                onClick={() => {
                  setOpenDoctorModal(true);
                }}
                disabled={doctorLimitReached || checkingLimit}
              >
                Add Doctor
              </Button>
            </span>
          </Tooltip>
        ) : null}
      </Grid>

      <Paper sx={{ width: "100%" }}>
        <Box sx={{ p: 1.5, pt: 1, pb: 1 }}>
          <Box sx={TABLE_SEARCH_ROW_SX}>
            <TextField
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={TABLE_ICON_SX} fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title={showFilters ? "Hide filters" : "Show filters"}>
              <IconButton
                size="small"
                sx={TABLE_ICON_BUTTON_SX}
                onClick={() => setShowFilters((v) => !v)}
              >
                <FilterAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export to Excel">
              <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={handleExport}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Refresh">
              <IconButton
                size="small"
                sx={TABLE_ICON_BUTTON_SX}
                onClick={async () => await fetchDoctorList(true)}
              >
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {showFilters && (
          <Stack spacing={1} direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mt: 1 }}>
            <TextField
              size="small"
              label="Filter by any field"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
            />
            <TextField
              size="small"
              label="Department"
              value={filterDepartment}
              onChange={(e) => setFilterDepartment(e.target.value)}
              select
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">All</MenuItem>
              {visibleDepartmentFilters.map((dept) => (
                <MenuItem key={dept.id} value={dept.name}>
                  {dept.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="From joining date"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              label="To joining date"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Tooltip title="Clear filters">
              <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={clearFilters}>
                <ClearAllIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          )}
        </Box>
        <TableContainer sx={{ maxHeight: 550 }}>
          <Table>
            <TableHead>
              <TableRow>
                {getColumns(user?.roleName).map((column) => (
                  <StyledTableCell
                    key={column.id}
                    align={column.align}
                    style={{ top: 57, minWidth: column.minWidth }}
                  >
                    {column.label}
                  </StyledTableCell>
                ))}
                <StyledTableCell key={"action"} style={{ top: 57 }}>
                  Action
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {doctor_list.length === 0 || fetching.doctor_list ? (
                <TableRow>
                  <TableCell colSpan={getColumns(user?.roleName).length + 1}>
                    {fetching.doctor_list && <CircularProgress size={40} />}
                    {!fetching.doctor_list && doctor_list.length === 0 && (
                      <Alert>No Doctor </Alert>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                doctor_list
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => {
                    const columns = getColumns(user?.roleName);
                    const rowActive = isDoctorRecordActive(row);
                    return (
                      <TableRow
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={row.id ?? row.code}
                        sx={{
                          opacity: rowActive ? 1 : 0.72,
                          backgroundColor: rowActive
                            ? "inherit"
                            : "action.hover",
                        }}
                      >
                        {columns.map((column) => {
                          if (column.id === "status") {
                            return (
                              <TableCell key="status">
                                {rowActive ? (
                                  <Chip label="Active" color="success" size="small" />
                                ) : (
                                  <Chip
                                    label="Deactivated"
                                    color="error"
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                              </TableCell>
                            );
                          }

                          let value =
                            column.id === "mobile" ||
                            column.id === "email" ||
                            column.id === "aadhar" ||
                            column.id === "password"
                              ? getDoctorFieldValue(row, column.id)
                              : row[column.id];
                          
                          // Format joiningDate for display
                          if (column.id === "joiningDate" && value) {
                            value = dayjs(value).isValid()
                              ? dayjs(value).format("DD-MM-YYYY")
                              : value;
                          }
                          
                          // Format userRole for display (capitalize first letter)
                          if (column.id === "userRole" && value) {
                            value = value.charAt(0).toUpperCase() + value.slice(1);
                          }
                          
                          return (
                            <TableCell key={column.id} align={column.align}>
                              {column.format && typeof value === "number"
                                ? column.format(value)
                                : (value === null || value === "" || value === undefined ? "-" : String(value))}
                            </TableCell>
                          );
                        })}
                        <TableCell key={`action-${row.id}`}>
                          <Box display="flex" alignItems="center">
                            {canManageDoctors && (
                              <>
                                <Tooltip title="Edit doctor">
                                  <span>
                                    <IconButton
                                      size="small"
                                      sx={TABLE_ICON_BUTTON_SX}
                                      disabled={statusUpdatingId === row.id}
                                      onClick={async () => {
                                        setDoctorInfo(row);
                                        setOpenDoctorModal(true);
                                        try {
                                          const full = await getDoctorById(row.id);
                                          setDoctorInfo((prev) => ({ ...prev, ...full }));
                                        } catch (e) {
                                          // non-blocking
                                        }
                                      }}
                                    >
                                      <ModeEditOutlineTwoToneIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                {rowActive ? (
                                  <Tooltip title="Deactivate doctor (blocks login)">
                                    <span>
                                      <IconButton
                                        sx={{ color: theme.palette.error.main }}
                                        disabled={statusUpdatingId === row.id}
                                        onClick={() => handleDeactivateDoctor(row)}
                                      >
                                        {statusUpdatingId === row.id ? (
                                          <CircularProgress size={22} />
                                        ) : (
                                          <PersonOffOutlinedIcon />
                                        )}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                ) : (
                                  <Tooltip title="Activate doctor">
                                    <span>
                                      <IconButton
                                        sx={{ color: theme.palette.success.main }}
                                        disabled={statusUpdatingId === row.id}
                                        onClick={() => handleActivateDoctor(row)}
                                      >
                                        {statusUpdatingId === row.id ? (
                                          <CircularProgress size={22} />
                                        ) : (
                                          <HowToRegOutlinedIcon />
                                        )}
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}
                              </>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={doctor_list.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      <AddNewDoctor
        open={open_doctor_modal}
        onClose={async () => {
          setOpenDoctorModal(false);
          setDoctorInfo(null);
          await fetchDoctorList(true);
        }}
        doctor_info={doctor_info}
      />
    </Grid>
  );
}
